var _vars    = require('../../modules/vars')._vars;
var difyApi  = require('../../modules/providers/dify/api');
var parser   = require('../../ui/formatters/caq-parser');
var renderer = require('./caq-renderer');

var CAQ_FALLBACK_TIMEOUT_MS = 10000; // used when data-caq-timeout is absent/invalid
var PROCESSED_ATTR = 'data-caq-processed';

// On any failure (error, non-ok, timeout, empty) the module must not be shown.
// The design contract is to add a `hide` class (CSS supplied separately).
function hideCaq(wrapper) {
    if (wrapper) {
        wrapper.classList.add('hide');
    }
}

// Randomize card order (Fisher-Yates) so the questions appear in a different
// order on each load. Shuffles a copy; does not mutate the input array.
function shuffleCards(cards) {
    var shuffled = cards.slice();
    for (var i = shuffled.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1));
        var tmp = shuffled[i];
        shuffled[i] = shuffled[j];
        shuffled[j] = tmp;
    }
    return shuffled;
}

// Fetch the CAQ questions with a hard timeout. On timeout the fetch is aborted and
// the promise rejects, so the caller hides the module.
function fetchCaqWithTimeout(articleContext, timeoutMs) {
    var controller = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var signal = controller ? controller.signal : undefined;
    var timeoutId;

    var timeoutPromise = new Promise(function(_, reject) {
        timeoutId = setTimeout(function() {
            if (controller) {
                controller.abort();
            }
            reject(new Error('CAQ_TIMEOUT'));
        }, timeoutMs);
    });

    var fetchPromise = difyApi.getCaqQuestions(articleContext, signal).then(function(res) {
        clearTimeout(timeoutId);
        return res;
    });

    return Promise.race([fetchPromise, timeoutPromise]);
}

// Fetch + render a single CAQ skeleton wrapper. Idempotent per wrapper.
function processCaqWrapper(wrapper) {
    if (!wrapper || wrapper.getAttribute(PROCESSED_ATTR) === 'true') {
        return;
    }
    wrapper.setAttribute(PROCESSED_ATTR, 'true');

    // This article's own context (guid/title/content-type), stamped per-wrapper by
    // the mixin via data-* attributes, so each A2A feed article fetches its OWN
    // questions with matching metadata.
    var articleContext = {
        guid: wrapper.getAttribute('data-article-guid'),
        title: wrapper.getAttribute('data-article-title'),
        contentTypeGuid: wrapper.getAttribute('data-content-type-guid')
    };

    // Timeout (ms) comes from the VUE_APP_CAQ_TIMEOUT config, passed by the mixin
    // via data-caq-timeout. Fall back to 10s when absent or invalid.
    var timeoutMs = parseInt(wrapper.getAttribute('data-caq-timeout'), 10);
    if (!timeoutMs || timeoutMs <= 0) {
        timeoutMs = CAQ_FALLBACK_TIMEOUT_MS;
    }

    // Ensure an auth token is available before fetching.
    var tokenPromise = _vars.token
        ? Promise.resolve()
        : difyApi.generateToken().then(function(tokenJSON) {
            if (tokenJSON && tokenJSON.token) {
                _vars.token = 'Bearer ' + tokenJSON.token;
            }
        });

    tokenPromise.then(function() {
        return fetchCaqWithTimeout(articleContext, timeoutMs).then(function(response) {
            if (!response || !response.ok) {
                hideCaq(wrapper);
                return;
            }
            return response.json().then(function(json) {
                var cards = parser.parseCaqCards(json);
                if (!cards.length) {
                    hideCaq(wrapper);
                    return;
                }
                renderer.renderCaqCarousel(shuffleCards(cards), wrapper);
            });
        });
    }).catch(function(error) {
        console.log('Error fetching CAQ:', error);
        hideCaq(wrapper);
    });
}

// Process every unprocessed CAQ skeleton currently in the DOM.
function processAllCaqWrappers() {
    document.querySelectorAll('.caq-wrapper.loader').forEach(processCaqWrapper);
}

// A2A feeds inject article bodies (and their CAQ skeletons) lazily as the user
// scrolls, and the Vue mixin injects the initial skeleton after its async auth
// poll — both may happen after boot. Watch the DOM and process new skeletons.
function observeForNewCaqWrappers() {
    if (typeof MutationObserver === 'undefined') {
        return;
    }
    var observer = new MutationObserver(function(mutations) {
        for (var i = 0; i < mutations.length; i++) {
            if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
                processAllCaqWrappers();
                return;
            }
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });
}

function initCaqModule() {
    try {
        processAllCaqWrappers();
        observeForNewCaqWrappers();
    } catch (error) {
        console.log('initCaqModule exception:', error);
    }
}

module.exports = {
    initCaqModule: initCaqModule
};